import { fabric } from 'fabric';
import { AssetVersionCommentDto } from '@api/Api';
import { highlightCreator } from '@pages/MediaViewer/AssetViewer/Canvas/Objects/highlightHandler';
import type { HighlightCreatorViewerActions } from '@pages/MediaViewer/AssetViewer/Canvas/Objects/highlightHandler';
import { pinPointCreator } from '@pages/MediaViewer/AssetViewer/Canvas/Objects/pinHandlers';
import { fitPinSize } from '@pages/MediaViewer/AssetViewer/Canvas/utils/fitObject';
import changeObjectPosition from '@pages/MediaViewer/AssetViewer/Canvas/utils/changeObjectsPosition';

type RenderPdfPageAnnotationsParams = {
  canvas: fabric.Canvas;
  pageNumber: number;
  commentsList: AssetVersionCommentDto[];
  zoomFactor: number;
  turnoverDegree: number;
  isHideComments: boolean;
  getUserPicture: (commentId: string) => {
    name: string;
    email: string;
    userId: string;
  };
  viewerActions: HighlightCreatorViewerActions;
  onSelectComment: (commentId: string) => void;
};

export function renderPdfPageAnnotationsOnCanvas({
  canvas,
  pageNumber,
  commentsList,
  zoomFactor,
  turnoverDegree,
  isHideComments,
  getUserPicture,
  viewerActions,
  onSelectComment
}: RenderPdfPageAnnotationsParams) {
  canvas.clear();
  canvas.setViewportTransform([zoomFactor * 10, 0, 0, zoomFactor * 10, 0, 0]);

  if (isHideComments) {
    canvas.requestRenderAll();
    return;
  }

  const comments = commentsList.filter(
    (comment) => (comment.documentPage ?? 1) === pageNumber && !comment.isCopied
  );
  const commentsWithAnnotations = comments.filter(
    (comment) => !!comment.annotations
  );
  const commentsWithDocumentHighlight = comments.filter(
    (comment) => !!comment.documentHighlightRects?.length
  );

  const pageViewerActions: HighlightCreatorViewerActions = {
    selectComment: (commentId) => {
      if (!commentId) {
        viewerActions.selectComment(null);
        return;
      }
      onSelectComment(commentId);
    },
    setCanvasMode: viewerActions.setCanvasMode,
    setDocumentHighlight: viewerActions.setDocumentHighlight
  };

  commentsWithAnnotations.forEach((comment) => {
    const parsed = JSON.parse(comment.annotations as string);
    const pin = parsed.find((item: { type?: string }) => item.type === 'pin');
    const annotation = pin || parsed[0];
    if (!annotation || typeof annotation.left !== 'number') return;

    const { email, name } = getUserPicture(comment.id);
    pinPointCreator({
      canvas,
      scale: fitPinSize(zoomFactor),
      commentId: comment.id,
      userEmail: email,
      userName: name,
      documentHighlight: comment.documentHighlight,
      documentHighlightRects: comment.documentHighlightRects,
      x: annotation.left,
      y: annotation.top,
      viewerActions: pageViewerActions,
      callback: () =>
        changeObjectPosition({
          canvas,
          angle: turnoverDegree
        })
    });
  });

  commentsWithDocumentHighlight.forEach((comment) => {
    highlightCreator({
      canvas,
      viewerActions: pageViewerActions,
      commentId: comment.id,
      documentHighlight: comment.documentHighlight,
      documentHighlightRects: comment.documentHighlightRects
    });
  });

  canvas.requestRenderAll();
}
